/**
 * Time Calculation Utilities for Proportional Calendar Rendering
 */

import { IAppointment } from '../types/appointments.types';

// Constants for visual calculations
// Constants for visual calculations
const SLOT_HEIGHT_COLLAPSED = 70; // Height of hour slot when collapsed (in pixels)
const SLOT_HEIGHT_EXPANDED = 280; // Total height when expanded (4 slots * 70px each)
const MINUTES_PER_HOUR = 60;

/**
 * Convert time string (HH:mm) to minutes since midnight
 */
export const timeToMinutes = (time: string): number => {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * MINUTES_PER_HOUR + (minutes || 0);
};

/**
 * Calculate vertical offset in pixels from the slot's top
 * @param startTime - Appointment start time (e.g., "08:20")
 * @param slotTime - Slot time (e.g., "08:00")
 * @param isExpanded - Whether the hour slot is expanded to show 15-min intervals
 */
export const calculateVerticalOffset = (
  startTime: string,
  slotTime: string,
  isExpanded: boolean
): number => {
  const startMinutes = timeToMinutes(startTime);
  const slotMinutes = timeToMinutes(slotTime);
  
  // Calculate how many minutes into the hour slot the appointment starts
  const minutesIntoSlot = startMinutes - slotMinutes;
  
  if (minutesIntoSlot < 0) {
    // Appointment starts before this slot
    return 0;
  }
  
  // Calculate proportional offset
  const slotHeight = isExpanded ? SLOT_HEIGHT_EXPANDED : SLOT_HEIGHT_COLLAPSED;
  const pixelsPerMinute = slotHeight / MINUTES_PER_HOUR;
  
  return Math.floor(minutesIntoSlot * pixelsPerMinute);
};

/**
 * Calculate height in pixels based on appointment duration
 * Only calculates the visible portion (07:00-23:00)
 * @param startTime - Appointment start time
 * @param duration - Duration in minutes
 * @param expandedHours - Record of which hours are expanded
 */
export const calculateHeight = (
  startTime: string,
  duration: number,
  expandedHours: Record<string, boolean> = {}
): number => {
  if (!duration || duration <= 0) {
    return 30; // Default minimum height
  }
  
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = startMinutes + duration;
  
  // Clamp to visible range: 07:00 (420 min) to 23:00 (1380 min)
  // Appointments after 23:00 are cut off at the end of the 22:00 slot
  const VISIBLE_START = 7 * 60;  // 07:00 in minutes
  const VISIBLE_END = 23 * 60;   // 23:00 in minutes
  
  const visibleStart = Math.max(startMinutes, VISIBLE_START);
  const visibleEnd = Math.min(endMinutes, VISIBLE_END);
  
  // If appointment is completely outside visible range, return minimum
  if (visibleStart >= visibleEnd) {
    return 20;
  }
  
  let totalHeight = 0;
  
  // Calculate height for each hour the appointment spans (within visible range)
  const startHour = Math.floor(visibleStart / 60);
  const endHour = Math.floor(visibleEnd / 60);
  
  for (let hour = startHour; hour <= endHour; hour++) {
    const hourStart = hour * 60;
    const hourEnd = (hour + 1) * 60;
    
    // Calculate how many minutes of this hour are covered by the appointment
    const overlapStart = Math.max(visibleStart, hourStart);
    const overlapEnd = Math.min(visibleEnd, hourEnd);
    const overlapMinutes = overlapEnd - overlapStart;
    
    if (overlapMinutes <= 0) continue;
    
    // Check if this specific hour is expanded
    const hourStr = hour.toString().padStart(2, '0');
    const isExpanded = expandedHours[hourStr] || false;
    
    const slotHeight = isExpanded ? SLOT_HEIGHT_EXPANDED : SLOT_HEIGHT_COLLAPSED;
    const pixelsPerMinute = slotHeight / MINUTES_PER_HOUR;
    
    totalHeight += overlapMinutes * pixelsPerMinute;
  }
  
  // Ensure minimum height for visibility
  return Math.max(20, Math.floor(totalHeight));
};

/**
 * Interface for appointment with calculated position
 */
export interface AppointmentPosition {
  appointmentId: number;
  columnIndex: number;
  totalColumns: number;
  startMinutes: number;
  endMinutes: number;
}

/**
 * Interface for overlap group
 */
export interface OverlapGroup {
  appointments: IAppointment[];
  startMinutes: number;
  endMinutes: number;
}

/**
 * Check if two time ranges overlap
 */
const doTimesOverlap = (
  start1: number,
  end1: number,
  start2: number,
  end2: number
): boolean => {
  return start1 < end2 && start2 < end1;
};

/**
 * Detect overlapping appointments and group them
 * @param appointments - List of appointments for a specific day
 */
export const detectOverlaps = (appointments: IAppointment[]): OverlapGroup[] => {
  if (appointments.length === 0) return [];
  
  // Convert appointments to time ranges
  const timeRanges = appointments.map(apt => {
    const startMinutes = timeToMinutes(apt.time || '07:00');
    let endMinutes: number;
    
    if (apt.endTime) {
      endMinutes = timeToMinutes(apt.endTime);
    } else if (apt.duration) {
      endMinutes = startMinutes + apt.duration;
    } else {
      endMinutes = startMinutes + 45; // Default 45 minutes
    }
    
    const VISIBLE_START = 7 * 60; // 420
    const VISIBLE_END = 23 * 60; // 1380

    let effectiveStart = Math.max(startMinutes, VISIBLE_START);
    let effectiveEnd = Math.min(endMinutes, VISIBLE_END);

    // Ensure minimum visual duration for overlap detection
    // This handles cases where an appointment is completely before 07:00 or clamped to 0 duration
    if (effectiveEnd <= effectiveStart) {
      effectiveEnd = effectiveStart + 15;
    }

    return {
      appointment: apt,
      startMinutes: effectiveStart,
      endMinutes: effectiveEnd
    };
  });
  
  // Sort by start time
  timeRanges.sort((a, b) => a.startMinutes - b.startMinutes);
  
  const groups: OverlapGroup[] = [];
  
  for (const range of timeRanges) {
    // Find if this appointment overlaps with any existing group
    let addedToGroup = false;
    
    for (const group of groups) {
      if (doTimesOverlap(range.startMinutes, range.endMinutes, group.startMinutes, group.endMinutes)) {
        // Add to this group
        group.appointments.push(range.appointment);
        // Expand group's time range
        group.startMinutes = Math.min(group.startMinutes, range.startMinutes);
        group.endMinutes = Math.max(group.endMinutes, range.endMinutes);
        addedToGroup = true;
        break;
      }
    }
    
    if (!addedToGroup) {
      // Create new group
      groups.push({
        appointments: [range.appointment],
        startMinutes: range.startMinutes,
        endMinutes: range.endMinutes
      });
    }
  }
  
  return groups;
};

/**
 * Calculate horizontal layout for overlapping appointments
 * Uses a column-based algorithm to minimize wasted space
 */
export const calculateHorizontalLayout = (
  group: OverlapGroup
): AppointmentPosition[] => {
  if (group.appointments.length === 1) {
    // Single appointment, takes full width
    return [{
      appointmentId: group.appointments[0].id,
      columnIndex: 0,
      totalColumns: 1,
      startMinutes: group.startMinutes,
      endMinutes: group.endMinutes
    }];
  }
  
  // Convert to time ranges with IDs, clamping to visible range (07:00-23:00)
  // this ensures appointments clamped to the same slot are treated as overlapping visually
  const VISIBLE_START = 7 * 60; // 420
  const VISIBLE_END = 23 * 60;  // 1380

  const ranges = group.appointments.map(apt => {
    const startMinutes = timeToMinutes(apt.time || '07:00');
    let endMinutes: number;
    
    if (apt.endTime) {
      endMinutes = timeToMinutes(apt.endTime);
    } else if (apt.duration) {
      endMinutes = startMinutes + apt.duration;
    } else {
      endMinutes = startMinutes + 45;
    }
    
    let effectiveStart = Math.max(startMinutes, VISIBLE_START);
    let effectiveEnd = Math.min(endMinutes, VISIBLE_END);

    // Enforce minimum visual duration for layout grouping (same as detectOverlaps)
    if (effectiveEnd <= effectiveStart) {
      effectiveEnd = effectiveStart + 15;
    }

    return {
      id: apt.id,
      startMinutes: effectiveStart,
      endMinutes: effectiveEnd
    };
  });
  
  // Sort by start time, then by end time
  ranges.sort((a, b) => {
    if (a.startMinutes !== b.startMinutes) {
      return a.startMinutes - b.startMinutes;
    }
    return a.endMinutes - b.endMinutes;
  });
  
  // Assign columns using greedy algorithm
  const columns: Array<{ endMinutes: number }> = [];
  const positions: AppointmentPosition[] = [];
  
  for (const range of ranges) {
    // Find first available column
    let columnIndex = 0;
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].endMinutes <= range.startMinutes) {
        // This column is free
        columnIndex = i;
        break;
      }
      columnIndex = i + 1;
    }
    
    // Add or update column
    if (columnIndex >= columns.length) {
      columns.push({ endMinutes: range.endMinutes });
    } else {
      columns[columnIndex].endMinutes = range.endMinutes;
    }
    
    positions.push({
      appointmentId: range.id,
      columnIndex,
      totalColumns: 0, // Will be set below
      startMinutes: range.startMinutes,
      endMinutes: range.endMinutes
    });
  }
  
  // Set total columns for all positions
  const totalColumns = columns.length;
  positions.forEach(pos => {
    pos.totalColumns = totalColumns;
  });
  
  return positions;
};

/**
 * Get the hour slot that contains this time
 * Clamps times before 07:00 to 07:00 and times 22:00+ to 22:00
 * @param time - Time string (HH:mm)
 * @returns Hour slot string (HH:00)
 */
export const getContainingHourSlot = (time: string): string => {
  if (!time) return '07:00';
  
  const [hoursStr, minutes] = time.split(':');
  let hours = parseInt(hoursStr, 10);
  
  // Clamp to visible range: 07:00 - 22:00
  if (hours < 7) {
    return '07:00';
  } else if (hours >= 22) {
    return '22:00';
  }
  
  return `${hours.toString().padStart(2, '0')}:00`;
};

/**
 * Calculate appointment end time from start time and duration
 * Handles appointments crossing midnight (e.g., 23:00 + 90 min = 00:30)
 */
export const calculateEndTime = (startTime: string, duration: number): string => {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = startMinutes + duration;
  
  // Handle midnight crossover with modulo 24 hours (1440 minutes)
  const normalizedMinutes = endMinutes % (24 * 60);
  const hours = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};
